import React from 'react';
import cx from 'classnames';
import styles from './Chip.module.scss';
import type { ChipProps } from './Chip.types';
import { WrapTextNodes } from '../../utils/WrapTextNodes';
import useBuildVariantProps from '../../../modules/theme/useBuildVariantProps';
import Icon from '../Icon';
import IconButton from '../IconButton';

/**
 * Chip component definition.
 */
const Chip = (props: ChipProps) => {
  const {
    className,
    type = 'Filled',
    style = 'A',
    size = 'Medium',
    leadingIcon,
    trailingIcon,
    closeIcon,
    isDisabled = false,
    onClick,
    onClose,
    children,
    corner = 'Rounded',
    isDismissible = false,
    ...restProps
  } = props;

  let leadingIconComponent, trailingIconComponent, content, closeIconComponent;

  // Render leading icon if provided
  if (leadingIcon) {
    leadingIconComponent = <div className={styles.leadingIcon}>{leadingIcon}</div>;
  }

  // Render trailing icon if provided
  if (trailingIcon) {
    trailingIconComponent = <div className={styles.trailingIcon}>{trailingIcon}</div>;
  }

  // Render close icon if dismissible
  if (isDismissible) {
    const closeIconView = closeIcon || <Icon icon="CloseDelete" style='Outlined' size='Small' />;
    closeIconComponent = (
      <IconButton
        type='Ghost'
        size='Small'
        onClick={(e) => {
          e?.stopPropagation();
          onClose?.(e);
        }}
        icon={closeIconView}
      />
    );
  }

  // Render chip content
  if (children) {
    content = (
      <div className={styles.content}>
        <WrapTextNodes noWrap={true} className={styles.text}>
          {children}
        </WrapTextNodes>
      </div>
    );
  }

  const variantProps = useBuildVariantProps(
    'chip',
    { type, size, style, corner },
    { isDisabled },
  );

  return (
    <div
      className={cx(
        styles.chip,
        className,
        {
          [styles.disabled]: isDisabled,
        },
      )}
      onClick={!isDisabled ? onClick : undefined}
      {...variantProps}
      {...restProps}
    >
      <div className={styles.iconAndContentContainer}>
        {leadingIconComponent}
        {content}
        {trailingIconComponent}
      </div>
      {closeIconComponent}
    </div>
  );
};

export default Chip;